import { useState, useEffect } from 'react';
import { Box, useBreakpointValue, IconButton } from '@chakra-ui/react';
import { Outlet } from 'react-router-dom';
import { HiBars3 } from 'react-icons/hi2';
import { Sidebar, type NavItem, type CurrentUser, type UserMenuItem } from './Sidebar';

interface AppLayoutProps {
  logo?: React.ReactNode;
  navItems: NavItem[];
  sidebarWidth?: string;
  currentUser?: CurrentUser;
  onUserMenuClick?: () => void;
  /** Menu items to show in the user dropdown */
  userMenuItems?: UserMenuItem[];
  /** Optional slot for sidebar actions (e.g., color mode toggle) */
  sidebarActions?: React.ReactNode;
  /** Optional slot rendered as a header bar above the main content */
  headerSlot?: React.ReactNode;
  /** When 'header', the user menu is hoisted out of the sidebar so it can be
   *  rendered by the page header instead. Defaults to 'sidebar' to preserve
   *  the legacy aegix-one-react chrome. */
  userMenuLocation?: 'sidebar' | 'header';
  /** Optional bottom nav item (e.g., Settings) shown above the user menu in
   *  the sidebar. Forwarded to Sidebar.bottomNavItem. */
  bottomNavItem?: NavItem;
}

export function AppLayout({
  logo,
  navItems,
  sidebarWidth = '220px',
  currentUser,
  onUserMenuClick,
  userMenuItems,
  sidebarActions,
  headerSlot,
  userMenuLocation = 'sidebar',
  bottomNavItem,
}: AppLayoutProps) {
  // Sidebar starts open on desktop, closed on mobile
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Initialize sidebar state based on screen size
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing breakpoint to sidebar state
    setIsSidebarOpen(isDesktop ?? false);
  }, [isDesktop]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    // Only auto-close on mobile
    if (!isDesktop) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <Box minH="100vh" bg="surface.page" position="relative">
      {/* Skip navigation link */}
      <a
        href="#main-content"
        className="skip-nav-link"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: 2000,
        }}
        onFocus={(e) => {
          Object.assign(e.currentTarget.style, {
            position: 'fixed',
            top: '16px',
            left: '16px',
            width: 'auto',
            height: 'auto',
            overflow: 'visible',
            padding: '8px 16px',
            borderRadius: '6px',
            fontWeight: '600',
            fontSize: '14px',
          });
        }}
        onBlur={(e) => {
          Object.assign(e.currentTarget.style, {
            position: 'absolute',
            left: '-9999px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          });
        }}
      >
        Skip to main content
      </a>
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        navItems={navItems}
        width={sidebarWidth}
        logo={logo}
        currentUser={currentUser}
        onUserMenuClick={onUserMenuClick}
        userMenuItems={userMenuItems}
        actions={sidebarActions}
        hideUserMenu={userMenuLocation === 'header'}
        bottomNavItem={bottomNavItem}
      />

      {/* Mobile menu toggle button */}
      {!isDesktop && (
        <IconButton
          aria-label="Toggle menu"
          onClick={toggleSidebar}
          position="fixed"
          top={4}
          left={4}
          zIndex={1060}
          display={{ base: 'flex', lg: 'none' }}
          size="sm"
          variant="solid"
          bg="surface.primary"
          borderWidth="1px"
          borderColor="border.primary"
        >
          <HiBars3 size={20} />
        </IconButton>
      )}

      <Box
        ml={{
          base: 0,
          lg: isSidebarOpen ? sidebarWidth : 0,
        }}
        transition="margin-left 200ms cubic-bezier(0.4, 0, 0.2, 1)"
        minH="100vh"
      >
        {headerSlot}
        <Box as="main" id="main-content" tabIndex={-1} p={{ base: 4, md: 6, lg: 8 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
